import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "bg-[#0A0A0A] border-[#27272A] rounded-none focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-[#A1A1AA]";

const NUM_FIELDS = [
  { k: "goals", l: "Goals" },
  { k: "assists", l: "Assists" },
  { k: "minutes", l: "Minutes" },
  { k: "appearances", l: "Apps" },
  { k: "yellow_cards", l: "Yellow" },
  { k: "red_cards", l: "Red" },
  { k: "sprint_speed", l: "Sprint (km/h)" },
  { k: "fitness_score", l: "Fitness /100" },
  { k: "distance_km", l: "Distance (km)" },
];
const SKILL_FIELDS = [
  { k: "passing", l: "Passing" },
  { k: "shooting", l: "Shooting" },
  { k: "dribbling", l: "Dribbling" },
  { k: "defending", l: "Defending" },
  { k: "stamina", l: "Stamina" },
];

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: today(), entry_type: "Match", opponent: "", note: "" });

export default function EntryForm({ open, onOpenChange, playerId, entry, onSaved }) {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(entry);

  useEffect(() => {
    if (entry) {
      const f = { date: entry.date, entry_type: entry.entry_type || "Match", opponent: entry.opponent || "", note: entry.note || "" };
      [...NUM_FIELDS, ...SKILL_FIELDS].forEach(({ k }) => {
        f[k] = entry[k] ?? "";
      });
      setForm(f);
    } else {
      setForm(blank());
    }
  }, [entry, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const num = (v) => (v === "" || v === undefined ? null : Number(v));
    const payload = {
      date: form.date,
      entry_type: form.entry_type,
      opponent: form.opponent || null,
      note: form.note || null,
    };
    [...NUM_FIELDS, ...SKILL_FIELDS].forEach(({ k }) => {
      payload[k] = num(form[k]);
    });
    try {
      if (isEdit) await api.put(`/players/${playerId}/entries/${entry.id}`, payload);
      else await api.post(`/players/${playerId}/entries`, payload);
      toast.success(isEdit ? "Entry updated" : "Session logged");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            {isEdit ? "Edit Session Data" : "Log Session Data"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="entry-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className={labelCls}>Date</Label>
              <Input data-testid="entry-date-input" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Type</Label>
              <Select value={form.entry_type} onValueChange={(v) => setForm((f) => ({ ...f, entry_type: v }))}>
                <SelectTrigger data-testid="entry-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A] rounded-none">
                  <SelectItem value="Match">Match</SelectItem>
                  <SelectItem value="Training">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={labelCls}>Opponent</Label>
              <Input value={form.opponent} onChange={set("opponent")} className={inputCls} />
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-3">Match & Physical</p>
            <div className="grid grid-cols-3 gap-3">
              {NUM_FIELDS.map(({ k, l }) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[11px] text-[#A1A1AA]">{l}</Label>
                  <Input data-testid={`entry-${k}-input`} type="number" step="any" value={form[k] ?? ""} onChange={set(k)} className={`${inputCls} h-9`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-3">Skill Ratings (1–100)</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {SKILL_FIELDS.map(({ k, l }) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[11px] text-[#A1A1AA]">{l}</Label>
                  <Input data-testid={`entry-${k}-input`} type="number" min="0" max="100" value={form[k] ?? ""} onChange={set(k)} className={`${inputCls} h-9`} />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className={labelCls}>Coach Note</Label>
            <Textarea value={form.note} onChange={set("note")} rows={2} className={inputCls} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" data-testid="entry-save-button" disabled={saving} className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider">
              {saving ? "Saving..." : isEdit ? "Update Entry" : "Save Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
